import { computed, nextTick, onMounted, onUnmounted, ref, type Ref } from 'vue'

export interface OnboardingStep {
  key: string
  icon?: string
  title: string
  subtitle?: string
}

export interface UseOnboardingOptions {
  steps: OnboardingStep[]
  startAtReview?: boolean
  scrollSelector?: string
  validateStep?: (stepIndex: number) => Promise<boolean>
  onBeforeAdvance?: (stepIndex: number) => void | Promise<void>
}

// Wait for Vue to render the next step before scrolling — scrolling before
// the new content is in the DOM races with the rerender (Safari's smooth-scroll
// animation gets canceled by the layout shift, leaving the user at the bottom).
// Setting scrollTop directly is instant and reliable across browsers.
const scrollToTop = async (selector: string) => {
  await nextTick()
  const el = document.querySelector(selector)
  if (el) el.scrollTop = 0
}

export function useOnboarding(options: UseOnboardingOptions) {
  const { steps } = options
  const totalSteps = steps.length
  const reviewStepIndex = totalSteps - 1
  const scrollSelector = options.scrollSelector ?? '.onboarding-scroll'

  const currentStep = ref(options.startAtReview ? reviewStepIndex : 0)
  const highestStepReached = ref(options.startAtReview ? reviewStepIndex : 0)
  const returnToReview = ref(false)
  const stepMenuOpen = ref(false)

  const currentMeta = computed(() => steps[currentStep.value])
  const isFirstStep = computed(() => currentStep.value === 0)
  const isLastStep = computed(() => currentStep.value === reviewStepIndex)
  const isStepReachable = (idx: number) => idx <= highestStepReached.value

  const goToStep = (idx: number) => {
    if (idx < 0 || idx >= totalSteps) return
    currentStep.value = idx
    if (idx > highestStepReached.value) highestStepReached.value = idx
    scrollToTop(scrollSelector)
  }

  const advance = () => {
    if (returnToReview.value) {
      returnToReview.value = false
      goToStep(reviewStepIndex)
    } else {
      goToStep(currentStep.value + 1)
    }
  }

  const back = () => {
    goToStep(currentStep.value - 1)
  }

  const navigateFromReview = (idx: number) => {
    returnToReview.value = true
    goToStep(idx)
  }

  const onStepClick = (idx: number) => {
    if (isStepReachable(idx)) {
      goToStep(idx)
      stepMenuOpen.value = false
    }
  }

  // ── Continue / Back handlers ──────────────────────────────────

  const onClickContinue = async () => {
    if (options.validateStep) {
      const valid = await options.validateStep(currentStep.value)
      if (!valid) return
    }

    const stepIndex = currentStep.value

    advance()

    if (options.onBeforeAdvance) {
      options.onBeforeAdvance(stepIndex)
    }
  }

  const onClickBack = () => {
    back()
  }

  const initialize = (step: number) => {
    currentStep.value = step
    highestStepReached.value = step
  }

  // ── Keyboard navigation ───────────────────────────────────────

  const onKeydown = async (e: KeyboardEvent) => {
    if (
      e.target instanceof HTMLInputElement ||
      e.target instanceof HTMLTextAreaElement ||
      e.target instanceof HTMLSelectElement
    )
      return

    if (e.key === 'ArrowLeft' && currentStep.value > 0) {
      e.preventDefault()
      back()
    } else if (e.key === 'ArrowRight' && currentStep.value < reviewStepIndex) {
      e.preventDefault()
      if (options.validateStep) {
        const valid = await options.validateStep(currentStep.value)
        if (!valid) return
      }
      const stepIndex = currentStep.value
      if (options.onBeforeAdvance) {
        options.onBeforeAdvance(stepIndex)
      }
      goToStep(currentStep.value + 1)
    }
  }

  onMounted(() => window.addEventListener('keydown', onKeydown))
  onUnmounted(() => window.removeEventListener('keydown', onKeydown))

  return {
    currentStep: currentStep as Readonly<Ref<number>>,
    highestStepReached: highestStepReached as Readonly<Ref<number>>,
    returnToReview: returnToReview as Readonly<Ref<boolean>>,
    stepMenuOpen,
    currentMeta,
    isFirstStep,
    isLastStep,
    totalSteps,
    reviewStepIndex,
    steps,
    isStepReachable,
    goToStep,
    advance,
    back,
    navigateFromReview,
    onStepClick,
    onClickContinue,
    onClickBack,
    initialize,
  }
}
